import type { EntityCategory, EntitySummary, EntityTemplate } from '../types/entities';
import type { ExportEntry } from '../types/export';
import { chapterExportOrder, chapterExportScenes } from './exportSelection';

const collator = new Intl.Collator('de-DE', { numeric: true, sensitivity: 'base' });

/** Build once per catalog change; comparison never depends on selection or click order. */
export function exportEntityOrder(
  entities: EntitySummary[],
  categories: EntityCategory[],
  templates: EntityTemplate[]
) {
  const categoryByKey = new Map(categories.map((category) => [category.key, category]));
  const templateById = new Map(templates.map((template) => [template.id, template]));
  const scenePosition = new Map<string, number>();
  for (const chapter of entities
    .filter((entity) => entity.status === 'active' && entity.templateCategory === 'kapitel')
    .sort(chapterExportOrder)) {
    for (const scene of chapterExportScenes(chapter, entities)) {
      if (!scenePosition.has(scene.id)) scenePosition.set(scene.id, scenePosition.size);
    }
  }
  // Narrative content is one group; chapters always precede standalone scenes.
  const groupKey = (entity: EntitySummary) =>
    entity.templateCategory === 'szene' ? 'kapitel' : entity.templateCategory;
  return (left: EntitySummary, right: EntitySummary) => {
    const leftKey = groupKey(left);
    const rightKey = groupKey(right);
    if (leftKey !== rightKey) {
      const a = categoryByKey.get(leftKey);
      const b = categoryByKey.get(rightKey);
      return (
        (a?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b?.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
        collator.compare(a?.name ?? leftKey, b?.name ?? rightKey) ||
        collator.compare(leftKey, rightKey)
      );
    }
    if (leftKey === 'kapitel') {
      if (left.templateCategory !== right.templateCategory) return left.templateCategory === 'kapitel' ? -1 : 1;
      if (left.templateCategory === 'kapitel')
        return chapterExportOrder(left, right) || collator.compare(left.id, right.id);
      const position =
        (scenePosition.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
        (scenePosition.get(right.id) ?? Number.MAX_SAFE_INTEGER);
      if (position) return position;
    } else if (left.templateId !== right.templateId) {
      const a = templateById.get(left.templateId);
      const b = templateById.get(right.templateId);
      return (
        (a?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b?.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
        collator.compare(a?.name ?? left.templateKey, b?.name ?? right.templateKey) ||
        collator.compare(left.templateId, right.templateId)
      );
    }
    return collator.compare(left.title, right.title) || collator.compare(left.id, right.id);
  };
}

export function sortExportEntries(
  entries: ExportEntry[],
  entities: EntitySummary[],
  compare: ReturnType<typeof exportEntityOrder>
) {
  const byId = new Map(entities.map((entity) => [entity.id, entity]));
  return [...entries].sort((left, right) => {
    const a = byId.get(left.entityId);
    const b = byId.get(right.entityId);
    return a && b ? compare(a, b) : a ? -1 : b ? 1 : 0;
  });
}
